import React, { useRef, useEffect, useCallback, useState } from 'react';
import type { Entity, EntityAction } from '../../types/entity';


interface EntityContextMenuProps {
  entity: Entity;
  actions: EntityAction[];
  position: { x: number; y: number };
  onSelect: (action: EntityAction, entity: Entity) => void;
  onClose: () => void;
}

export const EntityContextMenu: React.FC<EntityContextMenuProps> = ({ entity, actions, position, onSelect, onClose }) => {
  // Focus and keyboard navigation
  const menuRef = useRef<HTMLDivElement>(null);
  const [focusedIdx, setFocusedIdx] = useState(0);

  // Focus first menu item on open
  useEffect(() => {
    if (menuRef.current) {
      const firstBtn = menuRef.current.querySelector('button');
      if (firstBtn) (firstBtn as HTMLButtonElement).focus();
    }
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      setFocusedIdx((idx) => (idx + 1) % actions.length);
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setFocusedIdx((idx) => (idx - 1 + actions.length) % actions.length);
      e.preventDefault();
    } else if (e.key === 'Tab') {
      onClose();
    } else if (e.key === 'Enter' || e.key === ' ') {
      if (actions[focusedIdx]) {
        onSelect(actions[focusedIdx], entity);
      }
    }
  }, [actions, focusedIdx, onClose, onSelect, entity]);

  // Dismiss on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);


  return (
    <div
      className="entity-context-menu"
      ref={menuRef}
      role="menu"
      aria-label="Entity actions"
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        zIndex: 9999,
        background: '#fff',
        border: '1px solid #ccc',
        borderRadius: 8,
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        minWidth: 200,
        padding: 0,
        animation: 'fadeIn 0.15s',
        outline: 'none',
      }}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      <div style={{ padding: '10px 16px', fontWeight: 600, borderBottom: '1px solid #eee', background: '#f8f9fa', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>{entity.type}: {entity.value}</div>
      {actions.length === 0 ? (
        <div style={{ padding: 14, color: '#888' }}>No actions available</div>
      ) : (
        actions.map((action, idx) => (
          <button
            key={action.action}
            role="menuitem"
            style={{
              display: 'block',
              width: '100%',
              padding: '10px 16px',
              background: focusedIdx === idx ? '#f1f3f9' : 'none',
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              outline: focusedIdx === idx ? '2px solid #0078d4' : 'none',
              fontWeight: 500,
              color: '#222',
              borderRadius: 0,
              transition: 'background 0.15s',
            }}
            onClick={() => onSelect(action, entity)}
            tabIndex={-1}
            ref={el => {
              if (focusedIdx === idx && el) el.focus();
            }}
            aria-selected={focusedIdx === idx}
            onMouseEnter={() => setFocusedIdx(idx)}
          >
            {action.label}
          </button>
        ))
      )}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
